import { inject } from '@angular/core';
import { HttpContext } from '@angular/common/http';

import { Observable, of, map, forkJoin } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Apollo } from 'apollo-angular';
import { TranslateLoader, TranslationObject } from '@ngx-translate/core';

import { ITranslationsData, PhraseAppProject } from '../../models';
import { allTranslationsQuery } from './queries';
import { SKIP_AUTH } from '../../services/interceptors/auth.interceptor';

/**
 * Known translation key prefixes used in the translation files.
 * These will be stripped from keys to allow short key usage in templates.
 */
const TRANSLATION_PREFIXES = ['resident_saas', 'resident_shared', 'resident_naheimst'];

/**
 * Strips known prefixes from translation keys and converts to nested structure.
 * e.g., { "resident_saas.login_l": "value" } => { login_l: "value" }
 *
 * Keys are stored both with and without prefix for maximum compatibility.
 * Also creates nested structure for dot-separated keys after prefix removal.
 */
/**
 * Deep merges two translation objects. Values from `source` override values in `target`.
 * For nested objects, the merge is recursive.
 */
export function mergeTranslations(
  target: TranslationObject,
  source: TranslationObject
): TranslationObject {
  const result = { ...target };

  for (const key of Object.keys(source)) {
    const sourceValue = source[key];
    const targetValue = result[key];

    if (
      sourceValue &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue) &&
      targetValue &&
      typeof targetValue === 'object' &&
      !Array.isArray(targetValue)
    ) {
      result[key] = mergeTranslations(
        targetValue as TranslationObject,
        sourceValue as TranslationObject
      );
    } else {
      result[key] = sourceValue;
    }
  }

  return result;
}

export function normalizeTranslations(flat: TranslationObject): TranslationObject {
  const result: TranslationObject = {};

  for (const fullKey of Object.keys(flat)) {
    const value = flat[fullKey];

    // Keep the original flat key accessible
    result[fullKey] = value;

    // Strip known prefixes and add short key
    for (const prefix of TRANSLATION_PREFIXES) {
      if (fullKey.startsWith(`${prefix}.`)) {
        const shortKey = fullKey.substring(prefix.length + 1);
        result[shortKey] = value;

        // Also create nested structure for the short key
        const parts = shortKey.split('.');
        if (parts.length > 1) {
          let current: TranslationObject = result;
          for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i];
            if (!(part in current) || typeof current[part] !== 'object') {
              current[part] = {};
            }
            current = current[part] as TranslationObject;
          }
          current[parts[parts.length - 1]] = value;
        }
        break;
      }
    }
  }

  return result;
}

/**
 * Options for the base translation loader
 */
type TranslationLoaderOptions = {
  /**
   * Function to load translations from a local source
   * @param locale - The locale to load translations for
   * @returns Observable with the translations
   */
  loadLocalTranslations: (locale: string) => Observable<TranslationObject>;
};

/**
 * Creates a base translation loader that can be used with functional providers
 * @param options - Configuration options for the loader
 * @param apollo - Apollo client instance (injected by default)
 * @returns A TranslateLoader instance
 */
export function createBaseTranslateLoader(
  options: TranslationLoaderOptions,
  apollo = inject(Apollo)
): TranslateLoader {
  if (!apollo) {
    throw new Error('Apollo service is required');
  }

  const getTranslation = (locale = 'de'): Observable<TranslationObject> => {
    // 1. Load local translations first as base
    const local$ = options.loadLocalTranslations(locale).pipe(
      map(normalizeTranslations),
      catchError(error => {
        console.error('Error loading local translations:', error);
        return of({} as TranslationObject);
      })
    );

    // 2. Fetch online translations from GraphQL
    const online$ = apollo
      .query<{ getAllTranslations: ITranslationsData[] }>({
        query: allTranslationsQuery,
        variables: { locale, project: PhraseAppProject.RESIDENT },
        context: { httpContext: new HttpContext().set(SKIP_AUTH, true) },
      })
      .pipe(
        map(response => {
          if (!response?.data?.getAllTranslations?.[0]?.translations?.length) {
            return {} as TranslationObject;
          }
          return mapTranslations(response.data.getAllTranslations[0].translations);
        }),
        catchError(error => {
          console.error('Error loading online translations:', error);
          return of({} as TranslationObject);
        })
      );

    // 3. Merge: local first, then override/extend with online
    return forkJoin([local$, online$]).pipe(
      map(([localTranslations, onlineTranslations]) =>
        mergeTranslations(localTranslations, onlineTranslations)
      )
    );
  };

  const mapTranslations = (
    translations: { key: string; translation: string }[]
  ): TranslationObject => {
    const result: TranslationObject = {};
    translations.forEach(item => (result[item.key] = item.translation));
    return normalizeTranslations(result);
  };

  return {
    getTranslation,
  };
}
